import { useEffect, useRef, useState } from "react";

import type { JSONContent } from "@tiptap/react";

import { BASE_ASSETS } from "@assets/templates/constants";
import { getTemplateAssetCdnUrl } from "@assets/templates/utils";
import { CONTENT_RESET_META } from "@demo/extensions/utils/node-protection";
import { sendToModel } from "@demo/libs/model";
import {
  DEV_FIXTURE_KEY_PATTERN,
  DEV_FIXTURE_QUERY_PARAM,
  type DevFixture,
  type DevFixtureMedia,
} from "@demo/server/api/dev-fixtures/format";
import { useEditorRefStore } from "@demo/store/editor-ref";
import { DEFAULT_MEDIA_SET, useMediaStore } from "@demo/store/media";
import { useModelStore } from "@demo/store/model";
import { MediaData } from "@demo/types/base";

import { setPendingFixture } from "./pendingFixture";

export const TEST_FIXTURE_QUERY_PARAM = DEV_FIXTURE_QUERY_PARAM;
/** `slow` stretches every streaming state so the collapsed and expanded running cards can be inspected. */
export const TEST_FIXTURE_SPEED_QUERY_PARAM = "perceptron-test-fixture-speed";
export const FIXTURE_DIRECTORY = "/demo/assets/dev-fixtures";

/** Lets the editor commit the prompt before an `autoRun` fixture's Run reads it back. */
const AUTO_RUN_DELAY_MS = 50;

/**
 * Load a dev fixture (`server/api/dev-fixtures/format.ts`) into the composer.
 *
 * Activation: visit the app with `?perceptron-test-fixture=<key>`. The hook reads
 * `/demo/assets/dev-fixtures/<key>.json` (a developer's own, gitignored) or else `/demo/assets/dev-fixtures/<key>.example.json`
 * (tracked), applies the fixture's run settings, fills the composer with its prompt, and marks the
 * fixture for the next Run: the Run then goes through the real path and the server replays the
 * fixture's response in place of the model or the Agent harness. With `autoRun` it runs at once;
 * otherwise the developer presses Run. No-op in production or when neither file exists.
 *
 * The param is left on the URL so a refresh loads the same fixture, and the fixture stays marked
 * so every Run replays it.
 */
export const useDevTestData = (): void => {
  const userEditorRef = useEditorRefStore((s) => s.userEditorRef);
  const addMedia = useMediaStore((s) => s.addMedia);

  const [fixture, setFixture] = useState<DevFixture | null>(null);
  const fetchAttempted = useRef(false);
  const hasFilledComposer = useRef(false);

  // Resolve the active fixture once per mount.
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    if (fetchAttempted.current) return;

    const params = new URLSearchParams(window.location.search);
    const fixtureKey = params.get(TEST_FIXTURE_QUERY_PARAM);
    if (!fixtureKey) return;
    const speed = params.get(TEST_FIXTURE_SPEED_QUERY_PARAM) === "slow" ? "slow" : "normal";
    fetchAttempted.current = true;
    if (!DEV_FIXTURE_KEY_PATTERN.test(fixtureKey)) {
      console.warn(`[useDevTestData] Malformed fixture key "${fixtureKey}".`);
      return;
    }

    void fetchFixture(fixtureKey).then((loaded) => {
      if (!loaded) return;
      applyRunSettings(loaded);
      setPendingFixture({ key: fixtureKey, speed });
      setFixture(loaded);
    });
  }, []);

  // Fill the composer once the user-prompt editor has registered itself (it mounts asynchronously).
  useEffect(() => {
    if (!fixture) return;
    if (hasFilledComposer.current) return;
    const editor = userEditorRef?.editor;
    if (!editor) return;
    hasFilledComposer.current = true;

    const media = (fixture.userMedia ?? []).map(resolveFixtureMedia).filter((item) => item !== null);
    if (fixture.userInput || media.length) {
      // Register media in the user media set BEFORE setContent so the <mediaSetContainer> node
      // finds them when its node-view first reads.
      for (const item of media) addMedia(DEFAULT_MEDIA_SET.id, item);

      // `emitUpdate: true` is load-bearing — useContentChangeNotification listens on the editor's
      // `update` event to flip the "has content" flag that hides demo prompts and enables the
      // run/clear controls. contentReset exempts the full-doc swap from node protection;
      // addToHistory keeps it off the undo stack.
      editor
        .chain()
        .setMeta(CONTENT_RESET_META, true)
        .setMeta("addToHistory", false)
        .setContent(buildUserDoc(fixture.userInput, media), true)
        .run();
    }

    if (!fixture.autoRun) return;
    const timer = setTimeout(() => void sendToModel(), AUTO_RUN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [fixture, userEditorRef, addMedia]);
};

/** Force the fixture's run toggles for the session, after saved preferences were restored. */
function applyRunSettings(fixture: DevFixture): void {
  const settings = fixture.runSettings;
  const store = useModelStore.getState();
  if (settings?.enableThinking !== undefined) store.setEnableReasoning(settings.enableThinking);
  if (settings?.tools !== undefined) store.setEnabledTools(settings.tools);
}

/** A prompt media item as the composer needs it; a template asset resolves to its CDN URL. */
function resolveFixtureMedia(item: DevFixtureMedia): MediaData | null {
  if (!("template" in item)) return item;
  const asset = BASE_ASSETS[item.template as keyof typeof BASE_ASSETS];
  if (!asset) {
    console.warn(`[useDevTestData] Unknown template asset "${item.template}".`);
    return null;
  }
  return { data: "", extension: asset.extension, key: asset.uuid, url: getTemplateAssetCdnUrl(asset.uuid) };
}

async function fetchFixture(fixtureKey: string): Promise<DevFixture | null> {
  // The developer's own file wins; the tracked example lets a fresh checkout work as is.
  const candidates = [`${FIXTURE_DIRECTORY}/${fixtureKey}.json`, `${FIXTURE_DIRECTORY}/${fixtureKey}.example.json`];

  for (const url of candidates) {
    try {
      const response = await fetch(url, { cache: "no-store" });
      if (response.ok) return (await response.json()) as DevFixture;
    } catch (error) {
      console.warn(`[useDevTestData] Failed to load fixture from ${url}:`, error);
    }
  }

  console.warn(
    `[useDevTestData] No fixture for "${fixtureKey}". Looked at ${candidates.join(" then ")}. ` +
      `Drop a file at ${FIXTURE_DIRECTORY}/${fixtureKey}.json.`,
  );
  return null;
}

export function buildUserDoc(text: string | undefined, media: MediaData[]): JSONContent {
  const content: JSONContent[] = [];

  if (media.length > 0) {
    content.push({
      type: "mediaSetContainer",
      attrs: {
        id: DEFAULT_MEDIA_SET.id,
        mediaIds: media.map((m) => m.key),
      },
    });
  }

  if (text) {
    for (const line of text.split("\n")) {
      content.push({ type: "paragraph", content: line.length > 0 ? [{ type: "text", text: line }] : [] });
    }
  } else if (content.length === 0) {
    content.push({ type: "paragraph" });
  }

  return { type: "doc", content };
}
